import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, CheckCircle2, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface DocSpec {
  key: string;
  label: string;
  descricao: string;
}

const DOCS: DocSpec[] = [
  { key: 'rg', label: 'RG (Identidade)', descricao: 'Frente e verso, foto bem nítida' },
  { key: 'cpf', label: 'CPF', descricao: 'Pode ser o número impresso no RG' },
  { key: 'comprovante_residencia', label: 'Comprovante de residência', descricao: 'Conta de luz, água ou similar' },
  { key: 'carteira_trabalho', label: 'Carteira de Trabalho', descricao: 'Página com sua foto e dados' },
  { key: 'foto_3x4', label: 'Foto 3x4', descricao: 'Pode tirar uma selfie com fundo claro' },
  { key: 'titulo_eleitor', label: 'Título de Eleitor', descricao: 'Frente do documento' },
];

type Status = 'pendente' | 'enviando' | 'enviado' | 'aprovado' | 'reprovado';

export default function Documentos() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cpf, setCpf] = useState<string>('');
  const [statusMap, setStatusMap] = useState<Record<string, Status>>({});
  const inputsRef = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      const { data: contr } = await supabase
        .from('contratacao')
        .select('cpf')
        .eq('id_usuario', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (contr?.cpf) setCpf(String(contr.cpf).replace(/\D/g, ''));

      const { data: docs } = await supabase
        .from('documentos_contratacao')
        .select('tipo_documento, status_validacao')
        .eq('user_id', user.id);

      const map: Record<string, Status> = {};
      (docs || []).forEach((d: any) => {
        const s = d.status_validacao;
        if (s === 'aprovado') map[d.tipo_documento] = 'aprovado';
        else if (s === 'reprovado') map[d.tipo_documento] = 'reprovado';
        else map[d.tipo_documento] = 'enviado';
      });
      setStatusMap(map);
    })();
  }, [user?.id]);

  const handleFile = async (key: string, file: File | null) => {
    if (!file || !user?.id) return;
    if (!cpf) {
      toast.error('Não foi possível identificar sua contratação.');
      return;
    }
    setStatusMap((m) => ({ ...m, [key]: 'enviando' }));
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const fileName = `${cpf}/${key}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('contratacao-documentos')
        .upload(fileName, file, { upsert: true });
      if (upErr) throw upErr;

      const { error: dbErr } = await supabase
        .from('documentos_contratacao')
        .upsert(
          {
            user_id: user.id,
            cpf,
            tipo_documento: key,
            nome_arquivo: file.name,
            caminho_storage: fileName,
            status_validacao: 'pendente',
          },
          { onConflict: 'user_id,tipo_documento' },
        );
      if (dbErr) throw dbErr;

      setStatusMap((m) => ({ ...m, [key]: 'enviado' }));
      toast.success('Documento enviado!');
    } catch (e: any) {
      console.error(e);
      setStatusMap((m) => ({ ...m, [key]: 'pendente' }));
      toast.error('Não conseguimos enviar. Tente novamente.');
    }
  };

  const enviados = DOCS.filter((d) => ['enviado', 'aprovado'].includes(statusMap[d.key])).length;
  const todosEnviados = enviados === DOCS.length;

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">Seus documentos</h1>
        <p className="text-sm text-muted-foreground">
          Toque em cada item e tire uma foto. Você pode reenviar a qualquer momento.
        </p>
        <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${(enviados / DOCS.length) * 100}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {enviados} de {DOCS.length} enviados
        </p>
      </div>

      <div className="space-y-3">
        {DOCS.map((doc) => {
          const status = statusMap[doc.key] || 'pendente';
          return (
            <Card key={doc.key} className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{doc.label}</p>
                  <p className="text-xs text-muted-foreground">{doc.descricao}</p>
                  <div className="mt-2">
                    {status === 'aprovado' && (
                      <span className="inline-flex items-center text-xs text-green-600 gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Aprovado
                      </span>
                    )}
                    {status === 'enviado' && (
                      <span className="inline-flex items-center text-xs text-blue-600 gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Enviado, aguardando análise
                      </span>
                    )}
                    {status === 'reprovado' && (
                      <span className="inline-flex items-center text-xs text-destructive gap-1">
                        <AlertCircle className="h-3.5 w-3.5" /> Reenvie este documento
                      </span>
                    )}
                    {status === 'pendente' && (
                      <span className="text-xs text-muted-foreground">Aguardando envio</span>
                    )}
                    {status === 'enviando' && (
                      <span className="inline-flex items-center text-xs text-muted-foreground gap-1">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Enviando…
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <input
                ref={(el) => (inputsRef.current[doc.key] = el)}
                type="file"
                accept="image/*,application/pdf"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFile(doc.key, e.target.files?.[0] || null)}
              />
              <Button
                variant={status === 'pendente' || status === 'reprovado' ? 'default' : 'outline'}
                size="lg"
                className="w-full h-12 mt-3"
                disabled={status === 'enviando'}
                onClick={() => inputsRef.current[doc.key]?.click()}
              >
                <Camera className="h-4 w-4 mr-2" />
                {status === 'pendente' && 'Tirar foto / Enviar'}
                {status === 'enviando' && 'Enviando…'}
                {status === 'enviado' && 'Reenviar'}
                {status === 'aprovado' && 'Reenviar'}
                {status === 'reprovado' && 'Reenviar agora'}
              </Button>
            </Card>
          );
        })}
      </div>

      <Button
        size="lg"
        className="w-full h-14 text-base"
        disabled={!todosEnviados}
        onClick={() => navigate('/candidato/aguardando')}
      >
        {todosEnviados ? 'Concluir envio' : `Faltam ${DOCS.length - enviados} documento(s)`}
        {todosEnviados && <ArrowRight className="h-5 w-5 ml-2" />}
      </Button>
    </div>
  );
}
